import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  Button,
  CircularProgress,
  Divider,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import {
  CoordinatePatient,
  DispositionType,
  ClinicalNeed,
  UrgencyLevel,
  DISPOSITION_LABELS,
  CLINICAL_NEED_LABELS,
} from '~/types/coordinate';

interface Props {
  patient: CoordinatePatient;
  onDispositionChange: (d: DispositionType) => void;
  onNeedsChange: (needs: ClinicalNeed[]) => void;
  onUrgencyChange: (u: UrgencyLevel) => void;
  onRunMatching: () => void;
}

const ALL_NEEDS = Object.keys(CLINICAL_NEED_LABELS) as ClinicalNeed[];

const InteractionControls: React.FC<Props> = ({
  patient,
  onDispositionChange,
  onNeedsChange,
  onUrgencyChange,
  onRunMatching,
}) => {
  const isMatching = patient.coordinationStatus === 'matching';
  const canRunMatch =
    patient.coordinationStatus === 'pending' ||
    patient.coordinationStatus === 'confirm_failed' ||
    patient.coordinationStatus === 'declined';

  const toggleNeed = (need: ClinicalNeed) => {
    const current = patient.clinicalNeeds;
    if (current.includes(need)) {
      onNeedsChange(current.filter(n => n !== need));
    } else {
      onNeedsChange([...current, need]);
    }
  };

  return (
    <Paper sx={{ p: 2.5 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <TuneIcon fontSize="small" color="action" />
        <Typography variant="subtitle2" fontWeight={700}>
          Referral Setup
        </Typography>
      </Stack>

      {!patient.predictProgram && (
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            Care Disposition
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={patient.disposition}
              onChange={e => onDispositionChange(e.target.value as DispositionType)}
              disabled={isMatching}
            >
              {(Object.keys(DISPOSITION_LABELS) as DispositionType[]).map(d => (
                <MenuItem key={d} value={d}>{DISPOSITION_LABELS[d]}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      <Box sx={{ mb: 2.5 }}>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Clinical Needs
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {ALL_NEEDS.map(need => {
            const selected = patient.clinicalNeeds.includes(need);
            return (
              <Chip
                key={need}
                label={CLINICAL_NEED_LABELS[need]}
                size="small"
                color={selected ? 'primary' : 'default'}
                variant={selected ? 'filled' : 'outlined'}
                onClick={() => !isMatching && toggleNeed(need)}
                sx={{ cursor: isMatching ? 'default' : 'pointer' }}
              />
            );
          })}
        </Box>
      </Box>

      <Box sx={{ mb: 2.5 }}>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Urgency
        </Typography>
        <ToggleButtonGroup
          value={patient.urgency}
          exclusive
          size="small"
          onChange={(_, val) => val && onUrgencyChange(val as UrgencyLevel)}
          disabled={isMatching}
        >
          <ToggleButton value="routine">Routine</ToggleButton>
          <ToggleButton value="urgent" sx={{ '&.Mui-selected': { bgcolor: 'warning.light' } }}>
            Urgent
          </ToggleButton>
          <ToggleButton value="emergent" sx={{ '&.Mui-selected': { bgcolor: 'error.light' } }}>
            Emergent
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {canRunMatch && (
        <Button
          variant="contained"
          fullWidth
          onClick={onRunMatching}
          disabled={isMatching}
          startIcon={isMatching ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isMatching ? 'Finding Providers…' : 'Run Matching'}
        </Button>
      )}

      {patient.coordinationStatus === 'confirm_failed' && (
        <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 1, textAlign: 'center' }}>
          Previous provider unavailable — select another
        </Typography>
      )}

      {patient.coordinationStatus === 'declined' && (
        <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 1, textAlign: 'center' }}>
          Referral declined — run matching to find alternatives
        </Typography>
      )}
    </Paper>
  );
};

export default InteractionControls;
